import Image from 'next/image';
import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { usePopper } from 'react-popper';
import cn from 'classnames';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { CommonHelpers, RC_DURATION } from '@lambda/reebelo';
import {
  OrderInvoiceDTO,
  OrderInvoiceType,
} from '@lambda/apis/src/order/types';
import {
  InvoiceRequestByOrderVendor,
  invoiceRequestKey,
} from '@lambda/business/orders/invoiceRequestKey';
import DownloadInvoices from '@/components/accounts/Orders/my-orders/DownloadInvoices';
import RequestInvoiceCta from '@/components/accounts/Orders/my-orders/RequestInvoiceCta';
import { CtaButton } from '@/components/Cta/Cta';
import blackTick from '@/public/icons/account/blackTick.svg';
import placeholderProductImage from '@/public/images/smartForm/no-image-placeholder.svg';
import close from '@/public/icons/account/close.svg';
import {
  getDeliveryDateFromShipment,
  isWithinFreeReturnPeriod,
  isWithinWarrantyCoverage,
} from '@/lib/smartForm/orderUnitUtil';
import {
  CustomerOrder,
  LineItemsT,
  Shipment,
  ShipmentLineItem,
  ShipmentStatus,
} from '@/lib/smartForm/types';
import { handleExtractFulfillmentStatus } from '@/lib/smartForm/helper';
import FulfillmentStatusBar from './FulfillmentStatusBar';
import TrackingDetails from './TrackingDetail';
import settings from '@/settings/configs';
import { generateBatteryHealthTitle } from '@/lib/collections/helpers';
import i18n from '@/settings/i18n';

const t = i18n.init('orders');

const smartformButtonCommonCSS =
  'w-2/5 flex-1 border bg-white pt-2 pb-2 text-center text-sm font-bold text-gray-700 duration-200 ease-in-out sm:w-full sm:flex-none sm:font-normal lg:w-full lg:border lg:px-4 lg:pt-2 lg:pb-2 lg:!text-base';
const RETURN = 'return';
const WARRANTY_CLAIM = 'warrantyClaim';
const SELLER = 'Seller';
const REEBELOCARE_EXTENDED = 'ReebeloCare Extended';

const SmartformButton = ({
  buttonType,
  isCovered,
  orderNumber,
  lineItemId,
  shouldShowButton,
  rcOrderId,
}: {
  buttonType: typeof RETURN | typeof WARRANTY_CLAIM;
  isCovered: boolean;
  orderNumber: string;
  lineItemId: string;
  shouldShowButton: boolean;
  lineItem?: ShipmentLineItem;
  rcOrderId?: string;
}) => {
  const router = useRouter();
  const { store } = settings;

  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const [arrowElement, setArrowElement] = useState<any>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top-start',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        },
      },
      { name: 'offset', options: { offset: [0, 4] } },
    ],
  });

  const [isHover, setIsHover] = useState(false);

  const isUSStore = store === 'reebelo-us' || store === 'reebelo-dev';
  const warrantyLabel = isUSStore ? t`Warranty` : t`Warranty Claim`;

  return (
    <>
      {isHover && !isCovered && (
        <div
          ref={setPopperElement}
          // eslint-disable-next-line
          style={styles.popper}
          {...attributes.popper}
          className="z-40"
        >
          <div
            className={cn(
              'w-72 rounded-lg bg-gray-700 px-1 text-white',
              {
                'h-28': buttonType === RETURN,
              },
              {
                'h-24': buttonType === WARRANTY_CLAIM,
              },
            )}
          >
            <p className="p-4 text-sm">
              {buttonType === RETURN
                ? t`Your item can no longer be returned since it has exceeded the return period. Please submit a Warranty Claim instead.`
                : t`Your device is outside of the Warranty Coverage Period.`}
            </p>
            <div
              className={cn(
                'absolute rotate-45',
                {
                  'right-0 mr-8 mt-3 sm:right-auto sm:ml-8 sm:mr-auto':
                    buttonType === WARRANTY_CLAIM,
                },
                {
                  '-mt-3 ml-8': buttonType === RETURN,
                },
              )}
            >
              <span
                className="h-4 w-4 bg-gray-700"
                // eslint-disable-next-line
                style={styles.arrow}
                ref={setArrowElement}
              />
            </div>
          </div>
        </div>
      )}
      {isCovered && shouldShowButton ? (
        <CtaButton
          variant="white"
          className={cn(
            'border-gray-700 text-gray-700 hover:bg-gray-700 hover:text-white hover:!shadow-none lg:border-gray-700 lg:hover:text-white',
            smartformButtonCommonCSS,
          )}
          onClick={() => {
            router.push(
              `/account/order/${orderNumber}/${buttonType}?lineItemId=${lineItemId}${
                rcOrderId ? `&rcOrderId=${rcOrderId}` : ''
              }`,
              undefined,
              {
                shallow: true,
              },
            );
          }}
        >
          {' '}
          {buttonType === RETURN ? t`Return` : warrantyLabel}
        </CtaButton>
      ) : (
        <CtaButton
          variant="white"
          className={cn(
            'border-gray-700/40 text-gray-700/40 hover:!shadow-none lg:border-gray-700/40 ',
            smartformButtonCommonCSS,
          )}
          onMouseOver={() => setIsHover(true)}
          onMouseOut={() => setIsHover(false)}
          ref={setReferenceElement}
        >
          {buttonType === RETURN ? t`Return` : warrantyLabel}
        </CtaButton>
      )}
    </>
  );
};

const WarrantyInfo = ({
  isRefunded,
  isCoveredByWarranty,
  isReebeloCare,
  isItemDelivered,
  duration,
}: {
  isRefunded: boolean;
  isCoveredByWarranty: boolean;
  isReebeloCare: boolean;
  isItemDelivered: boolean;
  duration: RC_DURATION;
}) => {
  if (isRefunded) {
    return null;
  }

  const isUSStore = ['reebelo-dev', 'reebelo-us'].includes(settings.store);

  const warrantyText = (() => {
    // before item is delivered will always show covered text
    const provider = isReebeloCare ? t(REEBELOCARE_EXTENDED) : t(SELLER);

    const defaultWarrantyText = t`Covered by ${{ provider }} Warranty`;

    const usProvider = isReebeloCare
      ? t`ReebeloCare ${{ duration }}-Months' Extended`
      : t(SELLER);
    const UsWarrantyText = t`Covered by ${{ provider: usProvider }} Warranty`;

    const _warrantyText = isUSStore ? UsWarrantyText : defaultWarrantyText;

    if (isItemDelivered) {
      if (isCoveredByWarranty) {
        return _warrantyText;
      }

      return t`Out of ${{ provider }} Warranty's Coverage Period`;
    }

    return _warrantyText;
  })();

  const displayCoveredWarranty =
    !isItemDelivered || (isItemDelivered && isCoveredByWarranty);

  return (
    <div
      className={cn('flex w-full items-center', {
        'h-[11px]': !displayCoveredWarranty,
      })}
    >
      <div
        className={cn(
          'mr-1 flex w-[11px] items-center justify-center',
          {
            'h-[11px]': displayCoveredWarranty,
          },
          {
            'align-middle': !displayCoveredWarranty,
          },
        )}
      >
        {displayCoveredWarranty ? (
          <Image width={11} height={11} alt={t`tick`} src={blackTick} />
        ) : (
          <Image width={9} height={9} alt={t`close`} src={close} />
        )}
      </div>
      <span
        className={cn('w-full text-xs', {
          'mt-1': !displayCoveredWarranty,
        })}
      >
        {warrantyText}
      </span>
    </div>
  );
};

const OrderItem = ({
  deliveryDate,
  storeTimeZone,
  orderNumber,
  lineItem,
  reebeloCares,
  isRefunded,
  shipmentStatus,
  processedAt,
  lineItems,
}: {
  deliveryDate: string | null;
  storeTimeZone: string;
  orderNumber: string;
  lineItem: ShipmentLineItem;
  reebeloCares: string[];
  isRefunded: boolean;
  shipmentStatus: ShipmentStatus;
  processedAt: string | null;
  lineItems: LineItemsT;
}) => {
  const [imgSrc, setImgSrc] = useState(
    lineItem.image || placeholderProductImage,
  );

  const { batteryHealth } = extractTags(lineItem.tags);

  dayjs.extend(isSameOrAfter);

  const isCoveredByCG =
    lineItem.reebeloCare.isCovered &&
    lineItem.reebeloCare.reebeloCareVendor === 'Cover Genius';

  const isReebeloCare = reebeloCares.some((reebeloCare) =>
    reebeloCare.startsWith(`[${lineItem.sku}]`),
  );

  const reebeloCareLineItem = lineItems.find((l) => {
    const rcFor = l.properties?.find((att) => att.name === 'For')?.value;

    return (
      rcFor?.toLowerCase().includes(lineItem.name.toLowerCase()) ||
      rcFor?.includes(lineItem.sku)
    );
  });

  const reebeloCareDuration = (reebeloCareLineItem?.properties?.find(
    (att) => att.name === 'Duration',
  )?.value ?? '24') as RC_DURATION;

  const isCoveredByWarranty = isWithinWarrantyCoverage(
    deliveryDate,
    isReebeloCare,
    storeTimeZone,
    reebeloCareDuration,
  );

  const isCoveredByFreeReturn = isWithinFreeReturnPeriod(
    deliveryDate,
    storeTimeZone,
    processedAt,
  );

  const isItemDelivered = shipmentStatus === 'delivered';
  const isItemShipped = shipmentStatus === 'ordered';

  const spec = lineItem.name.split(' - ');

  if (batteryHealth) {
    spec.push(
      t`${{
        batteryHealthTitle: generateBatteryHealthTitle(batteryHealth),
      }} Battery`,
    );
  }
  const description = spec.slice(1).join(' - ');

  const getTitle = () => {
    const title = spec[0];

    return title;
  };

  const title = getTitle();

  // Date when CG Policy Creation was updated '2023-05-31T08:00:00+08:00' | RT-4753
  const isAfterCGUpdates = dayjs(processedAt)
    .utc()
    .isSameOrAfter(dayjs('2023-05-31T08:00:00+08:00').utc());

  const shouldShowWarrantyButton =
    isItemDelivered ||
    (isItemShipped && isReebeloCare && isCoveredByCG && isAfterCGUpdates);

  const displayQuantity = isRefunded
    ? lineItem.refundQuantity
    : lineItem.quantity - (lineItem.refundQuantity || 0);

  return (
    <div className="mb-3.5 w-full flex-wrap border-b border-gray-200 pb-3.5 last:border-b-0 sm:flex xl:flex-nowrap">
      <div className="mt-6 flex gap-4 sm:mt-0">
        <div className="mt-1 flex h-[100px] w-[100px] min-w-[100px] items-center justify-center xxs:min-w-[100px] xs:min-w-[100px]">
          <Image
            height={80}
            width={80}
            alt={t`product image`}
            className="mt-3 h-full object-contain"
            src={imgSrc}
            onError={() => {
              setImgSrc(placeholderProductImage);
            }}
          />
        </div>
        <div className="flex flex-row flex-wrap text-sm lg:py-4">
          <div className="mb-4">
            <h2 className="w-full font-bold">
              {t`${{ title }} (x${{ displayQuantity: displayQuantity ?? '' }})`}
            </h2>
            <span className="w-full">{description}</span>
          </div>
          <WarrantyInfo
            isRefunded={isRefunded}
            isReebeloCare={isReebeloCare}
            isCoveredByWarranty={isCoveredByWarranty}
            isItemDelivered={isItemDelivered}
            duration={reebeloCareDuration}
          />
        </div>
      </div>

      {!isRefunded && shouldShowWarrantyButton ? (
        <div className="flex w-full min-w-[175px] pl-0 pt-6 sm:block sm:pt-4 xl:w-auto xl:pl-2 xl:pt-0">
          <div className="contents w-full gap-4 space-x-2 sm:block sm:gap-0 sm:space-x-0 xl:w-auto">
            <SmartformButton
              buttonType="return"
              orderNumber={orderNumber}
              isCovered={isCoveredByFreeReturn}
              shouldShowButton={isItemDelivered}
              lineItemId={lineItem.id}
              rcOrderId={
                lineItem.reebeloCare.isCovered
                  ? lineItem.reebeloCare.rcOrderId
                  : undefined
              }
            />
            <SmartformButton
              buttonType="warrantyClaim"
              orderNumber={orderNumber}
              isCovered={isCoveredByWarranty}
              shouldShowButton={shouldShowWarrantyButton}
              lineItemId={lineItem.id}
              lineItem={lineItem}
              rcOrderId={
                lineItem.reebeloCare.isCovered
                  ? lineItem.reebeloCare.rcOrderId
                  : undefined
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** The line items a shipment view lists: its refunded units, or the rest. */
export const shipmentViewLineItems = (
  shipment: Shipment,
  isRefundedView: boolean,
) =>
  shipment.lineItems.filter((lineItem) =>
    isRefundedView
      ? lineItem.isRefunded && (lineItem.refundQuantity ?? 0) > 0
      : lineItem.quantity - (lineItem.refundQuantity ?? 0) > 0,
  );

export default function ShipmentDetail({
  shipment,
  shipmentIndex,
  processedAt,
  reebeloCares,
  order,
  storeTimeZone,
  lineItems,
  isRefundedView = false,
  orderInvoices,
  invoiceRequests,
  hideShipmentProofOfPurchase,
  proofOfPurchaseUrl,
}: {
  shipment: Shipment;
  shipmentIndex: number;
  processedAt: string;
  reebeloCares: string[];
  order: CustomerOrder;
  storeTimeZone: string;
  lineItems: LineItemsT;
  isRefundedView?: boolean;
  orderInvoices?: OrderInvoiceDTO[] | null;
  invoiceRequests?: InvoiceRequestByOrderVendor;
  /** The order card's order-level Proof of Purchase replaces this one. */
  hideShipmentProofOfPurchase?: boolean;
  /** The order-level Proof of Purchase, shown in this shipment's first row. */
  proofOfPurchaseUrl?: string;
}) {
  const {
    canceledAt,
    orderNumber,
    fulfillmentStatus,
    id: orderId,
    orderName,
    shopifyOrderId,
  } = order;
  const isSellerInvoiceStore = CommonHelpers.isSellerInvoiceStore(
    settings.store,
  );

  const vendors = Array.from(
    new Set(shipment.lineItems.flatMap((lineItem) => lineItem.vendor)),
  );

  // find the latest delivery date
  const shipmentDeliveryDate = getDeliveryDateFromShipment(shipment);

  const status: ShipmentStatus = handleExtractFulfillmentStatus(
    shipment,
    shipmentDeliveryDate,
    fulfillmentStatus,
  );

  /*
   * This following will handle edge cases where no tracking
   * is available for fulfilled orders from long time before
   */

  const deliveryDate =
    shipmentDeliveryDate === null && fulfillmentStatus === 'FULFILLED'
      ? processedAt
      : shipmentDeliveryDate;

  const isShipmentFullyRefunded =
    isRefundedView ||
    shipment.lineItems.every(
      (lineItem) =>
        lineItem.isRefunded && lineItem.quantity === lineItem.refundQuantity,
    );

  // The first row carries the order's Proof of Purchase; the rest show the
  // row's own document unless the order-level one replaces it.
  const hasFirstLink = (rowIndex: number) =>
    (rowIndex === 0 && Boolean(proofOfPurchaseUrl)) ||
    !hideShipmentProofOfPurchase;

  const bpfInvoice =
    orderInvoices?.find(
      (invoice) =>
        invoice.type === OrderInvoiceType.BUYER_PROTECTION_FEE &&
        invoice.vendorName === shipment.lineItems[0].vendor,
    ) ?? undefined;

  return (
    <>
      <div className="mt-3 w-full rounded border border-gray-200 bg-white sm:flex">
        <div className="w-full border-0 border-gray-200 px-4 py-3.5 sm:w-1/2 sm:border-r 2xl:w-1/2">
          {!isShipmentFullyRefunded && (
            <div>
              <div className="flex sm:flex-none">
                <h3 className="mb-3.5 mt-2 flex-1 text-sm font-bold leading-tight sm:mt-0 sm:flex-none">
                  {status !== 'delivered'
                    ? t`Awaiting Shipment`
                    : t`Shipment ${{ shipmentIndex }}`}
                </h3>

                <Link
                  href={`/account/order/${orderNumber}/help?vendorName=${encodeURIComponent(
                    vendors[0],
                  )}`}
                  className="mb-3.5 mr-5 mt-2 block h-3 w-3 text-right text-sm underline underline-offset-4 sm:hidden"
                >
                  {t`Help`}
                </Link>
              </div>
            </div>
          )}
          <div className="static mt-8 sm:mt-0 sm:hidden">
            <div className="-mt-8 w-full border border-solid border-gray-300"></div>
          </div>
          {shipmentViewLineItems(shipment, isRefundedView).map(
            (lineItem, idx) => {
              const sellerInvoice = orderInvoices?.find(
                (invoice) =>
                  invoice.type === OrderInvoiceType.SELLER_INVOICE &&
                  invoice.vendorName === lineItem.vendor,
              );
              // Built the same way the request is recorded, so a request raised
              // here is recognised on the next page load.
              const hasPendingInvoiceRequest =
                invoiceRequests?.[
                  invoiceRequestKey({
                    orderId,
                    vendorId: `${settings.store}#${lineItem.vendor}`,
                    type: OrderInvoiceType.SELLER_INVOICE,
                  })
                ] != null;

              return (
                <>
                  <OrderItem
                    key={idx}
                    lineItem={lineItem}
                    storeTimeZone={storeTimeZone}
                    deliveryDate={deliveryDate}
                    reebeloCares={reebeloCares}
                    orderNumber={orderNumber}
                    isRefunded={isRefundedView}
                    shipmentStatus={status}
                    processedAt={processedAt}
                    lineItems={lineItems}
                  />
                  <div className="justify-between font-bold sm:flex sm:flex-wrap sm:items-center sm:gap-y-1">
                    <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                      {hasFirstLink(idx) && (
                        <span className="block w-full sm:my-auto sm:w-auto">
                          {idx === 0 && proofOfPurchaseUrl ? (
                            <DownloadInvoices
                              invoiceUrl={proofOfPurchaseUrl}
                              invoiceType={OrderInvoiceType.STANDARD}
                              isProofOfPurchase
                            />
                          ) : (
                            <DownloadInvoices
                              invoiceUrl={
                                orderInvoices?.find(
                                  (invoice) =>
                                    invoice.type ===
                                      OrderInvoiceType.STANDARD &&
                                    invoice.vendorName === lineItem.vendor,
                                )?.url ?? undefined
                              }
                              invoiceType={OrderInvoiceType.STANDARD}
                            />
                          )}
                        </span>
                      )}
                      {bpfInvoice?.url && (
                        <>
                          {hasFirstLink(idx) && (
                            <span className="my-auto hidden sm:block">
                              {' | '}
                            </span>
                          )}
                          <span className="block w-full sm:my-auto sm:w-auto">
                            <DownloadInvoices
                              invoiceUrl={bpfInvoice.url}
                              invoiceType={
                                OrderInvoiceType.BUYER_PROTECTION_FEE
                              }
                            />
                          </span>
                        </>
                      )}
                      {isSellerInvoiceStore && lineItem.vendor && (
                        <>
                          {(hasFirstLink(idx) || bpfInvoice?.url) && (
                            <span className="my-auto hidden sm:block">
                              {' | '}
                            </span>
                          )}
                          <span className="block w-full sm:my-auto sm:w-auto">
                            {sellerInvoice?.url ? (
                              <DownloadInvoices
                                invoiceUrl={sellerInvoice.url}
                                invoiceType={OrderInvoiceType.SELLER_INVOICE}
                              />
                            ) : (
                              <RequestInvoiceCta
                                orderId={orderId}
                                orderName={orderName || orderNumber}
                                orderNumber={orderNumber}
                                shopifyOrderId={String(
                                  shopifyOrderId ?? orderId,
                                )}
                                processedAt={processedAt}
                                vendorName={lineItem.vendor}
                                lineItems={shipment.lineItems}
                                productNames={shipment.lineItems.map(
                                  (li) => li.name,
                                )}
                                hasPendingInvoiceRequest={
                                  hasPendingInvoiceRequest
                                }
                              />
                            )}
                          </span>
                        </>
                      )}
                    </div>
                    <Link
                      href={`/account/order/${orderNumber}/help?vendorName=${encodeURIComponent(
                        lineItem.vendor,
                      )}`}
                      className="hidden sm:block sm:py-2"
                    >
                      {t`Help`}
                    </Link>
                  </div>
                </>
              );
            },
          )}
        </div>
        <div className="flex w-full flex-wrap items-start border-r border-gray-200 sm:w-1/2 2xl:w-1/2">
          <FulfillmentStatusBar
            status={status}
            isRefunded={isRefundedView}
            canceledAt={canceledAt}
            orderedDate={processedAt}
            storeTimeZone={storeTimeZone}
            deliveredDate={shipment.deliveredBy}
            dispatchedDate={shipment.dispatchedAt}
          />
          {canceledAt == null && !isRefundedView && (
            <TrackingDetails
              shipment={shipment}
              shipmentStatus={status}
              storeTimeZone={storeTimeZone}
            />
          )}
        </div>
      </div>
    </>
  );
}
